import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { Search, SlidersHorizontal, X, Map, LayoutGrid } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import DealCard from '../components/DealCard';
import MapSection from '../components/MapSection';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = ['all', 'Food & Drink', 'Nightlife', 'Fitness & Wellness', 'Activities', 'Free Deals', 'Convenience Stores', 'Liquor Stores'];
const PERIODS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
];

export default function ExplorePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [deals, setDeals] = useState([]);
  const [total, setTotal] = useState(0);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'map'

  const search = searchParams.get('search') || '';
  const category = searchParams.get('category') || 'all';
  const cityId = searchParams.get('city') || 'all';
  const period = searchParams.get('period') || 'all';

  useEffect(() => {
    fetch(`${API}/cities`)
      .then((r) => r.json())
      .then((data) => setCities(data))
      .catch(() => {});
  }, []);

  const fetchDeals = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (category && category !== 'all') params.set('category', category);
      if (cityId && cityId !== 'all') params.set('city_id', cityId);
      if (period && period !== 'all') params.set('period', period);
      params.set('page', pg);
      params.set('limit', '12');

      const res = await fetch(`${API}/deals?${params.toString()}`);
      const data = await res.json();
      if (pg === 1) setDeals(data.deals || []);
      else setDeals((prev) => [...prev, ...(data.deals || [])]);
      setTotal(data.total || 0);
    } catch {}
    setLoading(false);
  }, [search, category, cityId, period]);

  useEffect(() => {
    setPage(1);
    fetchDeals(1);
  }, [fetchDeals]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') newParams.set(key, value);
    else newParams.delete(key);
    setSearchParams(newParams);
  };

  const clearAllFilters = () => {
    setSearchParams({});
  };

  const hasFilters = search || (category && category !== 'all') || (cityId && cityId !== 'all') || (period && period !== 'all');

  return (
    <div className="min-h-screen bg-[#F6F1E9]">
      <SEO
        title="Explore Student Deals in Athens, GA"
        description="Browse food, nightlife, fitness and local deals for students in Athens, Georgia. Filter by category, distance and more on Perkly."
        canonical="/explore"
      />
      <Navbar />
      <div className="bg-white border-b border-[#8A9A8B]/15 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <h1
            className="text-3xl sm:text-4xl font-bold text-[#2B2B2B] mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="explore-title"
          >
            Explore Deals
          </h1>

          {/* Search + Filter toggle */}
          <div className="flex gap-3 mb-5">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A9A8B]" />
              <input
                data-testid="explore-search-input"
                type="text"
                placeholder="Search deals, food, events..."
                defaultValue={search}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') updateFilter('search', e.target.value);
                }}
                onBlur={(e) => updateFilter('search', e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-full border border-[#8A9A8B]/30 bg-[#F6F1E9] text-sm focus:outline-none focus:border-[#C8A97E] focus:ring-2 focus:ring-[#C8A97E]/20"
              />
            </div>
            <button
              data-testid="filter-toggle-button"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-5 py-3 rounded-full border border-[#8A9A8B]/30 bg-white text-sm font-semibold text-[#2B2B2B] hover:border-[#C8A97E] transition-colors"
            >
              <SlidersHorizontal size={16} />
              <span className="hidden sm:inline">Filters</span>
              {hasFilters && <span className="w-2 h-2 rounded-full bg-[#C8A97E]" />}
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="bg-[#F6F1E9] rounded-2xl p-5 border border-[#8A9A8B]/15 mb-4 space-y-4" data-testid="filters-panel">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* City */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-2">City</label>
                  <select
                    data-testid="filter-city-select"
                    value={cityId}
                    onChange={(e) => updateFilter('city', e.target.value)}
                    className="w-full perkly-input text-sm py-2.5"
                  >
                    <option value="all">All Cities</option>
                    {cities.filter(c => c.is_active).map((c) => (
                      <option key={c.city_id} value={c.city_id}>{c.full_name || `${c.name}, ${c.state}`}</option>
                    ))}
                  </select>
                </div>

                {/* Category */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-2">Category</label>
                  <select
                    data-testid="filter-category-select"
                    value={category}
                    onChange={(e) => updateFilter('category', e.target.value)}
                    className="w-full perkly-input text-sm py-2.5"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c === 'all' ? 'All Categories' : c}</option>
                    ))}
                  </select>
                </div>

                {/* Time Period */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-2">Time Period</label>
                  <select
                    data-testid="filter-period-select"
                    value={period}
                    onChange={(e) => updateFilter('period', e.target.value)}
                    className="w-full perkly-input text-sm py-2.5"
                  >
                    {PERIODS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {hasFilters && (
                <button onClick={clearAllFilters} className="flex items-center gap-1.5 text-xs text-red-500 font-semibold hover:text-red-600" data-testid="clear-filters-button">
                  <X size={13} /> Clear All Filters
                </button>
              )}
            </div>
          )}

          {/* Category pills — horizontal scroll on mobile, wrap on desktop */}
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 sm:overflow-visible sm:flex-wrap sm:mx-0 sm:px-0 scrollbar-hide" data-testid="category-pills">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                data-testid={`category-pill-${cat}`}
                onClick={() => updateFilter('category', cat)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 ${
                  category === cat
                    ? 'bg-[#2B2B2B] text-[#F6F1E9]'
                    : 'bg-[#8A9A8B]/15 text-[#2B2B2B] hover:bg-[#C8A97E]/20'
                }`}
              >
                {cat === 'all' ? 'All Deals' : cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Deals Grid */}
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-[#2B2B2B]/60" data-testid="deals-count">
            {loading ? 'Loading...' : `${total} deal${total !== 1 ? 's' : ''} found`}
          </p>
          {/* View toggle */}
          <div className="flex items-center gap-1 bg-white rounded-xl border border-[#8A9A8B]/20 p-1" data-testid="view-toggle">
            <button
              data-testid="grid-view-button"
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${viewMode === 'grid' ? 'bg-[#2B2B2B] text-[#F6F1E9]' : 'text-[#2B2B2B]/60'}`}
            >
              <LayoutGrid size={13} /> List
            </button>
            <button
              data-testid="map-view-button"
              onClick={() => setViewMode('map')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${viewMode === 'map' ? 'bg-[#2B2B2B] text-[#F6F1E9]' : 'text-[#2B2B2B]/60'}`}
            >
              <Map size={13} /> Map
            </button>
          </div>
        </div>

        {/* MAP VIEW */}
        {viewMode === 'map' && (
          <MapSection deals={deals} />
        )}

        {/* GRID VIEW */}
        {viewMode === 'grid' && (
          <>
          {loading && page === 1 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-[#8A9A8B]/10" />
                <div className="p-4 space-y-2">
                  <div className="h-3 bg-[#8A9A8B]/10 rounded w-1/3" />
                  <div className="h-4 bg-[#8A9A8B]/10 rounded w-3/4" />
                  <div className="h-3 bg-[#8A9A8B]/10 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
          ) : deals.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="deals-grid">
              {deals.map((deal) => (
                <DealCard key={deal.deal_id} deal={deal} />
              ))}
            </div>
            {deals.length < total && (
              <div className="text-center mt-10">
                <button
                  data-testid="load-more-button"
                  onClick={() => {
                    const nextPage = page + 1;
                    setPage(nextPage);
                    fetchDeals(nextPage);
                  }}
                  className="btn-primary px-10 py-3.5"
                >
                  Load More Deals
                </button>
              </div>
            )}
          </>
          ) : (
          <div className="text-center py-20" data-testid="no-deals-message">
            <p className="text-5xl mb-4">🔍</p>
            <h3 className="text-xl font-bold text-[#2B2B2B] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>No Deals Found</h3>
            <p className="text-[#2B2B2B]/55 mb-6">Try adjusting your filters or search terms.</p>
            <button onClick={clearAllFilters} className="btn-primary">Clear Filters</button>
          </div>
          )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
